import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  CardBody,
  Button,
  Badge,
  Divider,
  Radio,
  RadioGroup,
  Stack,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import { FiCreditCard, FiDollarSign, FiCheckCircle, FiAlertCircle, FiSettings } from "react-icons/fi";
import { useGetPaymentMethod, useUpdatePaymentMethod, useGetMandateStatus } from "../../services/payrollService";
import DirectDebitSetup from "./DirectDebitSetup";

const PaymentMethodPreference = () => {
  const toast = useToast();
  const { isOpen: isSetupOpen, onOpen: onSetupOpen, onClose: onSetupClose } = useDisclosure();
  
  const { data: paymentMethodData, isLoading: paymentMethodLoading, refetch: refetchPaymentMethod } = useGetPaymentMethod();
  const { data: mandateStatusData, isLoading: mandateLoading, refetch: refetchMandateStatus } = useGetMandateStatus();
  
  // Refetch mandate status on mount to ensure fresh data
  useEffect(() => {
    refetchMandateStatus();
    refetchPaymentMethod();
  }, [refetchMandateStatus, refetchPaymentMethod]);

  // Debug logging to check mandate status data
  useEffect(() => {
    if (mandateStatusData) {
      console.log("Mandate Status Data:", mandateStatusData);
      console.log("Mandate object:", mandateStatusData?.mandate);
      console.log("Mandate status:", mandateStatusData?.mandate?.status);
      console.log("Has mandate:", mandateStatusData?.has_mandate);
    }
  }, [mandateStatusData]);
  
  const [selectedMethod, setSelectedMethod] = useState(
    paymentMethodData?.payment_method || "wallet"
  );

  // Polling state for mandate status after redirect
  const pollingIntervalRef = useRef(null);
  const pollingStartTimeRef = useRef(null);
  const [isPollingMandate, setIsPollingMandate] = useState(false);

  const { updatePaymentMethodPayload, updatePaymentMethodIsLoading, updatePaymentMethodError } = useUpdatePaymentMethod(() => {
    refetchPaymentMethod();
    toast({
      title: "Success",
      description: "Payment method preference updated successfully",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  });

  // Update selected method when data loads
  React.useEffect(() => {
    if (paymentMethodData?.payment_method) {
      setSelectedMethod(paymentMethodData.payment_method);
    }
  }, [paymentMethodData]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingStartTimeRef.current = null;
    setIsPollingMandate(false);
  }, []);

  // Check for pending mandate on mount (after redirect from Flutterwave)
  useEffect(() => {
    const pendingMandateCode = sessionStorage.getItem('pending_mandate_code') || 
                               localStorage.getItem('pending_mandate_code_backup');
    
    if (pendingMandateCode && !isPollingMandate) {
      console.log("Found pending mandate code, starting status check:", pendingMandateCode);
      setIsPollingMandate(true);
      
      // Show info toast
      toast({
        title: "Checking Authorization Status",
        description: "Verifying your Direct Debit authorization...",
        status: "info",
        duration: 3000,
        isClosable: true,
      });

      // Start polling immediately
      startMandateStatusPolling(pendingMandateCode);
    }

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll mandate status after redirect from Flutterwave
  const startMandateStatusPolling = useCallback(async (mandateCode) => {
    if (!mandateCode) {
      const storedCode = sessionStorage.getItem('pending_mandate_code') || 
                       localStorage.getItem('pending_mandate_code_backup');
      if (!storedCode) {
        stopPolling();
        return;
      }
      mandateCode = storedCode;
    }

    if (!pollingStartTimeRef.current) {
      pollingStartTimeRef.current = Date.now();
    }

    const POLLING_INTERVAL = 4000; // 4 seconds (same as wallet funding)
    const MAX_POLLING_TIME = 60000; // 60 seconds max
    const MAX_ATTEMPTS = 15; // ~60 seconds (15 attempts * 4 seconds)

    const pollMandateStatus = async () => {
      try {
        const { data: updatedStatus } = await refetchMandateStatus();
        const mandate = updatedStatus?.mandate;
        const mandateStatus = mandate?.status?.toLowerCase();
        
        // Check if mandate is active
        const isActive = 
          mandateStatus === "active" ||
          updatedStatus?.has_active_mandate === true ||
          updatedStatus?.has_mandate === true && mandateStatus === "active";

        const elapsed = Date.now() - (pollingStartTimeRef.current || Date.now());
        const attempts = Math.floor(elapsed / POLLING_INTERVAL) + 1;

        if (isActive) {
          // Mandate is active - success!
          console.log("✅ Mandate is now active!");
          stopPolling();
          
          // Clear stored mandate code
          sessionStorage.removeItem('pending_mandate_code');
          localStorage.removeItem('pending_mandate_code_backup');
          
          // Refetch payment method data
          await refetchPaymentMethod();
          
          // Auto-switch to direct_debit if not already selected
          const currentPaymentMethod = paymentMethodData?.payment_method;
          if (currentPaymentMethod !== "direct_debit") {
            try {
              await updatePaymentMethodPayload({ payment_method: "direct_debit" });
              setSelectedMethod("direct_debit");
            } catch (error) {
              console.error("Failed to auto-update payment method:", error);
              // Don't show error - user can manually select later
            }
          }
          
          toast({
            title: "✅ Direct Debit Authorized!",
            description: "Your Direct Debit mandate is now active and has been set as your default payment method.",
            status: "success",
            duration: 8000,
            isClosable: true,
          });
          
          return;
        }

        // Check if we've exceeded time or attempts
        if (elapsed >= MAX_POLLING_TIME || attempts >= MAX_ATTEMPTS) {
          stopPolling();
          console.log("⏱️ Polling timeout - mandate status:", mandateStatus);
          
          toast({
            title: "Authorization Processing",
            description: "Your authorization is being processed. The status will update shortly. You can check back in a few moments.",
            status: "info",
            duration: 8000,
            isClosable: true,
          });
          
          // Clear stored code after timeout (user can check manually)
          sessionStorage.removeItem('pending_mandate_code');
          localStorage.removeItem('pending_mandate_code_backup');
          
          return;
        }

        // Continue polling
        pollingIntervalRef.current = setTimeout(pollMandateStatus, POLLING_INTERVAL);
      } catch (error) {
        console.error("Error polling mandate status:", error);
        const elapsed = Date.now() - (pollingStartTimeRef.current || Date.now());
        
        if (elapsed >= MAX_POLLING_TIME) {
          stopPolling();
          toast({
            title: "Status Check Failed",
            description: "Unable to verify mandate status. Please check your mandate status manually.",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        } else {
          // Continue polling on error
          pollingIntervalRef.current = setTimeout(pollMandateStatus, POLLING_INTERVAL);
        }
      }
    };

    // Start polling immediately
    pollMandateStatus();
  }, [refetchMandateStatus, refetchPaymentMethod, updatePaymentMethodPayload, stopPolling]);

  const handleMethodChange = async (method) => {
    // Check for active mandate using the same logic
    const hasActiveMandateCheck = 
      mandateStatusData?.has_mandate === true ||
      mandateStatusData?.mandate?.status?.toLowerCase() === "active" ||
      paymentMethodData?.has_active_mandate === true ||
      false;
    
    if (method === "direct_debit" && !hasActiveMandateCheck) {
      // Show setup flow
      onSetupOpen();
      return;
    }

    setSelectedMethod(method);
    await updatePaymentMethodPayload({ payment_method: method });
  };

  const handleSetupSuccess = async () => {
    // Refetch to get latest mandate status
    const { data: updatedMandateStatus } = await refetchMandateStatus();
    const { data: updatedPaymentMethod } = await refetchPaymentMethod();
    
    // Auto-switch to direct debit after successful setup
    // Only auto-update if mandate is active (may not be in sandbox with mock mandates)
    const mandate = updatedMandateStatus?.mandate;
    const isActive = 
      updatedMandateStatus?.has_mandate === true ||
      mandate?.status?.toLowerCase() === "active" || 
      updatedMandateStatus?.has_active_mandate === true ||
      updatedPaymentMethod?.has_active_mandate === true;
    
    if (isActive) {
      // Only auto-update if mandate is active (backend validation requirement)
      updatePaymentMethodPayload({ payment_method: "direct_debit" });
      setSelectedMethod("direct_debit");
      
      // Show success message
      toast({
        title: "Direct Debit Setup Complete",
        description: "Your Direct Debit mandate is now active and has been set as your default payment method.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } else {
      // In sandbox or if status still pending, user can manually select later
      // This prevents the 400 error when trying to set direct_debit with pending mandate
      console.log("Mandate not yet active, skipping auto-update. User can select manually when status becomes active.");
      
      // Show info message for pending status
      toast({
        title: "Setup In Progress",
        description: "Your Direct Debit authorization is being processed. The status will update to 'active' shortly. You can select Direct Debit once it becomes active.",
        status: "info",
        duration: 6000,
        isClosable: true,
      });
    }
    
    // Scroll to top of payment method section for better visibility
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  if (paymentMethodLoading || mandateLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color="gray.600">
          Loading payment method preferences...
        </Text>
      </Box>
    );
  }

  const currentMethod = paymentMethodData?.payment_method || "wallet";
  // Check for active mandate from multiple sources:
  // 1. mandateStatusData.mandate.status === "active" (primary check)
  // 2. mandateStatusData.mandate.authorized_at exists (indicates authorization completed)
  // 3. paymentMethodData.has_active_mandate (from payment method endpoint)
  // 4. mandateStatusData.has_mandate === true AND mandate exists (fallback)
  const mandate = mandateStatusData?.mandate;
  const mandateStatus = mandate?.status?.toLowerCase();
  const hasActiveMandate = 
    mandateStatus === "active" ||
    (mandateStatusData?.has_mandate === true && mandate && mandateStatus === "active") ||
    (mandate?.authorized_at && mandateStatus !== "pending" && mandateStatus !== "PENDING") ||
    paymentMethodData?.has_active_mandate === true ||
    false;

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <VStack align="start" spacing={2}>
        <Heading size="md" color="gray.900" fontWeight="600">
          Payment Method Preference
        </Heading>
        <Text color="gray.600" fontSize="sm">
          Choose your default payment method for payroll processing. You can change this at any time.
        </Text>
      </VStack>

      {/* Payment Method Selection */}
      <Card>
        <CardBody>
          <RadioGroup
            value={selectedMethod}
            onChange={(value) => handleMethodChange(value)}
            isDisabled={updatePaymentMethodIsLoading}
          >
            <Stack spacing={4}>
              {/* Wallet Option */}
              <Box
                p={4}
                border="2px solid"
                borderColor={selectedMethod === "wallet" ? "blue.500" : "gray.200"}
                borderRadius="md"
                bg={selectedMethod === "wallet" ? "blue.50" : "white"}
                cursor="pointer"
                _hover={{ borderColor: "blue.300" }}
                onClick={() => handleMethodChange("wallet")}
              >
                <HStack spacing={4}>
                  <Radio value="wallet" colorScheme="blue" />
                  <VStack align="start" spacing={2} flex={1}>
                    <HStack>
                      <FiDollarSign size={20} color={selectedMethod === "wallet" ? "#3182ce" : "#718096"} />
                      <Text fontWeight="600" fontSize="md">
                        Wallet Funding
                      </Text>
                      {currentMethod === "wallet" && (
                        <Badge colorScheme="blue" variant="subtle" size="sm">
                          Current Default
                        </Badge>
                      )}
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      Fund your wallet and use the balance for payroll payments
                    </Text>
                  </VStack>
                  {selectedMethod === "wallet" && (
                    <FiCheckCircle size={24} color="#3182ce" />
                  )}
                </HStack>
              </Box>

              {/* Direct Debit Option */}
              <Box
                p={4}
                border="2px solid"
                borderColor={
                  selectedMethod === "direct_debit"
                    ? hasActiveMandate
                      ? "blue.500"
                      : "orange.500"
                    : "gray.200"
                }
                borderRadius="md"
                bg={
                  selectedMethod === "direct_debit"
                    ? hasActiveMandate
                      ? "blue.50"
                      : "orange.50"
                    : "white"
                }
                cursor={hasActiveMandate ? "pointer" : "not-allowed"}
                opacity={hasActiveMandate ? 1 : 0.7}
                _hover={hasActiveMandate ? { borderColor: "blue.300" } : {}}
                onClick={() => hasActiveMandate && handleMethodChange("direct_debit")}
              >
                <HStack spacing={4}>
                  <Radio
                    value="direct_debit"
                    colorScheme="blue"
                    isDisabled={!hasActiveMandate}
                  />
                  <VStack align="start" spacing={2} flex={1}>
                    <HStack>
                      <FiCreditCard
                        size={20}
                        color={
                          selectedMethod === "direct_debit"
                            ? hasActiveMandate
                              ? "#3182ce"
                              : "#dd6b20"
                            : "#718096"
                        }
                      />
                      <Text fontWeight="600" fontSize="md">
                        Direct Debit
                      </Text>
                      {currentMethod === "direct_debit" && hasActiveMandate && (
                        <Badge colorScheme="blue" variant="subtle" size="sm">
                          Current Default
                        </Badge>
                      )}
                      {!hasActiveMandate && (
                        <Badge colorScheme="orange" variant="subtle" size="sm">
                          Setup Required
                        </Badge>
                      )}
                      {hasActiveMandate && mandate?.status === "active" && (
                        <Badge colorScheme="green" variant="subtle" size="sm">
                          Active
                        </Badge>
                      )}
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                      {hasActiveMandate
                        ? `Authorized account: ${mandate?.account_number || "N/A"} (${mandate?.bank_name || "N/A"})`
                        : "Authorize direct debit from your bank account"}
                    </Text>
                  </VStack>
                  {selectedMethod === "direct_debit" && hasActiveMandate && (
                    <FiCheckCircle size={24} color="#3182ce" />
                  )}
                  {!hasActiveMandate && (
                    <Button
                      size="sm"
                      colorScheme="orange"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSetupOpen();
                      }}
                    >
                      Setup
                    </Button>
                  )}
                </HStack>
              </Box>
            </Stack>
          </RadioGroup>

          {/* Direct Debit Status Info */}
          {hasActiveMandate && mandate && (
            <>
              <Divider my={4} />
              <VStack align="start" spacing={3}>
                <Text fontSize="sm" fontWeight="600" color="gray.700">
                  Direct Debit Details
                </Text>
                <HStack spacing={4} fontSize="sm">
                  <Text color="gray.600">
                    <strong>Account:</strong> {mandate.account_number}
                  </Text>
                  <Text color="gray.600">
                    <strong>Bank:</strong> {mandate.bank_name}
                  </Text>
                  <Text color="gray.600">
                    <strong>Status:</strong>{" "}
                    <Badge
                      colorScheme={
                        mandate.status === "active" ? "green" : "orange"
                      }
                      variant="subtle"
                      size="sm"
                    >
                      {mandate.status === "active" ? "Active" : "Pending"}
                    </Badge>
                  </Text>
                </HStack>
                {mandate.authorized_at && (
                  <Text fontSize="xs" color="gray.500">
                    Authorized on:{" "}
                    {new Date(mandate.authorized_at).toLocaleDateString()}
                  </Text>
                )}
              </VStack>
            </>
          )}

          {/* Info Alert */}
          <Alert status="info" borderRadius="md" mt={4}>
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">Note</AlertTitle>
              <AlertDescription fontSize="xs">
                You can change the payment method when processing each payroll
                transaction. This is your default preference.
              </AlertDescription>
            </Box>
          </Alert>
        </CardBody>
      </Card>

      {/* Direct Debit Setup Modal */}
      <DirectDebitSetup
        isOpen={isSetupOpen}
        onClose={onSetupClose}
        onSuccess={handleSetupSuccess}
      />
    </VStack>
  );
};

export default PaymentMethodPreference;

